const QUERY_FLAG = 'desktopAssessmentTest';
const QUERY_MODE = 'desktopAssessmentMode';
const STORAGE_FLAG = 'flcDesktopAssessmentTest';
const STORAGE_MODE = 'flcDesktopAssessmentMode';
const STORAGE_PREFIX = 'flcDesktopAssessment';

export const DESKTOP_ASSESSMENT_MODES = {
  MOBILE: 'mobile',
  ONLINE: 'online',
};

export const DEFAULT_COLLAPSED_AUTHORED_ACTIVITY_MAPPING = {
  1: ['phonics_1_segmenting'],
  2: ['phonics_2_letter_naming'],
  3: ['phonics_3_blending'],
  4: ['phonics_4_word_reading'],
  5: ['reading_1_fluency', 'reading_1_comprehension'],
  6: ['reading_2_fluency', 'reading_2_comprehension'],
  7: ['reading_3_fluency', 'reading_3_comprehension'],
  8: ['vocabulary_1'],
  9: ['vocabulary_2'],
  10: ['vocabulary_3'],
  11: ['writing_1'],
};

export const DEFAULT_DIRECT_ACTIVITY_PLAN = [
  'phonics_1_segmenting',
  'phonics_2_letter_naming',
  'phonics_3_blending',
  'phonics_4_word_reading',
  'reading_1_fluency',
  'reading_1_comprehension',
  'reading_2_fluency',
  'reading_2_comprehension',
  'reading_3_fluency',
  'reading_3_comprehension',
  'vocabulary_1',
  'vocabulary_2',
  'vocabulary_3',
  'writing_1',
];

export const DEFAULT_DIRECT_AUTHORED_ACTIVITY_MAPPING = DEFAULT_DIRECT_ACTIVITY_PLAN.reduce(
  (mapping, activityKey, index) => ({ ...mapping, [index + 1]: [activityKey] }),
  {},
);

export const DEFAULT_AUTHORED_ACTIVITY_MAPPING = DEFAULT_COLLAPSED_AUTHORED_ACTIVITY_MAPPING;

function normalizeAssessmentLabel(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/&nbsp;/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9#\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractAssessmentOrdinal(label = '') {
  const text = normalizeAssessmentLabel(label);
  const direct = text.match(/(?:#|number\s*)\s*(\d{1,3})/i);
  if (direct) return Number(direct[1]);

  const trailing = text.match(/(?:phonics|vocabulary|writing|comprehension|fluency|reading)\s*(\d{1,3})/i);
  if (trailing) return Number(String(trailing[1]).charAt(0));

  const any = text.match(/\b(\d{1,3})\b/);
  return any ? Number(String(any[1]).charAt(0)) : null;
}

export function getAssessmentActivityKeyFromLabel(label = '') {
  const text = normalizeAssessmentLabel(label);
  const ordinal = extractAssessmentOrdinal(text);

  if (/phonics/.test(text) && ordinal >= 1 && ordinal <= 4) {
    return DEFAULT_DIRECT_ACTIVITY_PLAN[ordinal - 1] || null;
  }

  if (/vocab|vocabulary/.test(text) && ordinal >= 1 && ordinal <= 3) {
    return `vocabulary_${ordinal}`;
  }

  if (/reading/.test(text) || /fluency|comprehension/.test(text)) {
    if (/fluency/.test(text) && ordinal >= 1 && ordinal <= 3) {
      return `reading_${ordinal}_fluency`;
    }

    if (/comprehension/.test(text) && ordinal >= 1 && ordinal <= 3) {
      return `reading_${ordinal}_comprehension`;
    }
  }

  if (/writing/.test(text)) {
    return 'writing_1';
  }

  return null;
}

function getActivityById(solutionMenu = {}, activityId) {
  const activities = Array.isArray(solutionMenu?.activities) ? solutionMenu.activities : [];
  return activities.find((activity) => String(activity?._id) === String(activityId)) || null;
}

function getActivityLabelForMapping(activity = {}) {
  return [
    activity?.activityName,
    activity?.formattedName,
    activity?.name,
    activity?.title,
  ].filter(Boolean).join(' ');
}

function buildNameBasedAssessmentMapping(leafSlots = [], solutionMenu = {}) {
  const mapping = {};
  const seenKeys = new Set();

  leafSlots.forEach((slot, index) => {
    const activity = getActivityById(solutionMenu, slot.activityId);
    const label = getActivityLabelForMapping(activity || slot.item || {});
    const activityKey = getAssessmentActivityKeyFromLabel(label);

    if (activityKey && !seenKeys.has(activityKey)) {
      mapping[index + 1] = [activityKey];
      seenKeys.add(activityKey);
    }
  });

  const required = new Set(DEFAULT_DIRECT_ACTIVITY_PLAN);
  const hasAllDirectKeys = DEFAULT_DIRECT_ACTIVITY_PLAN.every((key) => seenKeys.has(key));

  return hasAllDirectKeys && seenKeys.size === required.size ? mapping : null;
}

function normalizeMode(value, fallback = DESKTOP_ASSESSMENT_MODES.ONLINE) {
  if (value === DESKTOP_ASSESSMENT_MODES.MOBILE) return DESKTOP_ASSESSMENT_MODES.MOBILE;
  if (value === DESKTOP_ASSESSMENT_MODES.ONLINE) return DESKTOP_ASSESSMENT_MODES.ONLINE;
  return fallback;
}

function getAssessmentQueryState(search = window.location.search) {
  const params = new URLSearchParams(search || '');
  return {
    enabledFlag: params.get(QUERY_FLAG),
    modeFlag: params.get(QUERY_MODE),
  };
}

export function syncDesktopAssessmentFlag(search = window.location.search) {
  const { enabledFlag, modeFlag } = getAssessmentQueryState(search);

  if (enabledFlag === '1') {
    localStorage.setItem(STORAGE_FLAG, '1');
  } else if (enabledFlag === '0') {
    localStorage.setItem(STORAGE_FLAG, '0');
  }

  if (modeFlag) {
    localStorage.setItem(STORAGE_MODE, normalizeMode(modeFlag));
  }

  return localStorage.getItem(STORAGE_FLAG);
}

export function isDesktopAssessmentEnabled(search = window.location.search, solutionMenu = null) {
  const storedFlag = syncDesktopAssessmentFlag(search);
  const { enabledFlag } = getAssessmentQueryState(search);

  if (enabledFlag === '0' || storedFlag === '0') return false;
  if (enabledFlag === '1' || storedFlag === '1') return true;

  // Production online assessment is available when the selected Solution menu has
  // a Level 0 row that points at an ActivityGroup. Do not rely on activitygroup.name:
  // production groups frequently have technical names like "48r&rer_2".
  return !!findFreeTrialActivityGroup(solutionMenu);
}

export function getDesktopAssessmentMode(search = window.location.search) {
  syncDesktopAssessmentFlag(search);
  const params = new URLSearchParams(search || '');
  const modeValue = params.get(QUERY_MODE) || localStorage.getItem(STORAGE_MODE);
  const normalized = normalizeMode(modeValue, DESKTOP_ASSESSMENT_MODES.ONLINE);
  localStorage.setItem(STORAGE_MODE, normalized);
  return normalized;
}

export function getDesktopAssessmentStorageKey({ familyMemberId, solutionId, mode } = {}) {
  return `${STORAGE_PREFIX}:${familyMemberId || 'unknown'}:${solutionId || 'no-solution'}:${normalizeMode(mode)}`;
}

export function loadDesktopAssessmentState(params = {}) {
  try {
    const rawValue = localStorage.getItem(getDesktopAssessmentStorageKey(params));
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    console.warn('[desktop-assessment][load-state-error]', error);
    return null;
  }
}

export function saveDesktopAssessmentState(params = {}, value) {
  try {
    localStorage.setItem(getDesktopAssessmentStorageKey(params), JSON.stringify(value || null));
  } catch (error) {
    console.warn('[desktop-assessment][save-state-error]', error);
  }
}

export function clearDesktopAssessmentState(params = {}) {
  try {
    localStorage.removeItem(getDesktopAssessmentStorageKey(params));
  } catch (error) {
    console.warn('[desktop-assessment][clear-state-error]', error);
  }
}

function getSolutionScope(solutionMenu = {}) {
  return solutionMenu?.scopeId || solutionMenu?.scope || null;
}

function getFirstCategoryItems(solutionMenu = {}) {
  const scope = getSolutionScope(solutionMenu);
  return Array.isArray(scope?.firstCategory?.items) ? scope.firstCategory.items : [];
}

function getFirstCategoryTitleMap(solutionMenu = {}) {
  return getFirstCategoryItems(solutionMenu).reduce((acc, item) => {
    if (item?.id) {
      acc[String(item.id)] = String(item.title || item.name || '');
    }
    return acc;
  }, {});
}

function getGroupById(solutionMenu = {}, groupId) {
  const groups = Array.isArray(solutionMenu?.activityGroups) ? solutionMenu.activityGroups : [];
  return groups.find((group) => String(group?._id) === String(groupId)) || null;
}

function decorateAssessmentGroup(group, row, categoryTitle = '') {
  if (!group) return null;
  return {
    ...group,
    __assessmentMenuRow: row || null,
    __assessmentCategoryTitle: categoryTitle || '',
  };
}

function getLevel0Rows(solutionMenu = {}) {
  const menuGroups = Array.isArray(solutionMenu?.menuGroups) ? solutionMenu.menuGroups : [];
  return menuGroups.filter((row) => String(row?.menuLevel) === '0' && row?.groupId);
}

function findAssessmentMenuRow(solutionMenu = {}) {
  const level0Rows = getLevel0Rows(solutionMenu);

  if (!level0Rows.length) return null;

  // Production contract v8: no custom DB markers and no ActivityGroup.name matching.
  // Diagnostic Assessment is resolved from the existing menu model only:
  // selected solution -> solutionMenu.menuGroups -> the Level 0 row -> groupId.
  // If there is more than one Level 0 row, the data is ambiguous for a single
  // Start Assessment CTA, so we fail closed instead of guessing.
  if (level0Rows.length === 1) {
    return level0Rows[0];
  }

  return null;
}

export function getAssessmentResolutionDiagnostics(solutionMenu = {}) {
  const level0Rows = getLevel0Rows(solutionMenu);
  const categoryTitles = getFirstCategoryTitleMap(solutionMenu);

  return {
    level0RowCount: level0Rows.length,
    level0Rows: level0Rows.map((row) => ({
      groupId: row?.groupId || null,
      menuLevel: row?.menuLevel || null,
      firstCategory: row?.firstCategory || null,
      firstCategoryTitle: categoryTitles[String(row?.firstCategory)] || '',
    })),
  };
}

export function findFreeTrialActivityGroup(solutionMenu) {
  if (!solutionMenu) return null;

  const row = findAssessmentMenuRow(solutionMenu);
  const categoryTitles = getFirstCategoryTitleMap(solutionMenu);

  if (row?.groupId) {
    const group = getGroupById(solutionMenu, row.groupId);
    if (group) {
      return decorateAssessmentGroup(group, row, categoryTitles[String(row.firstCategory)] || '');
    }
  }

  // No name-based fallback here. ActivityGroup.name is not business metadata in this DB.
  return null;
}

export function getAssessmentMenuRow(solutionMenu, activityGroup) {
  if (activityGroup?.__assessmentMenuRow) return activityGroup.__assessmentMenuRow;

  const rows = Array.isArray(solutionMenu?.menuGroups) ? solutionMenu.menuGroups : [];
  return rows.find((row) => String(row?.groupId) === String(activityGroup?._id)) || null;
}

export function getAssessmentRootSlotItems(activityGroup = {}) {
  return Array.isArray(activityGroup?.elements) ? activityGroup.elements : [];
}

export function getAssessmentRootSlotCount(activityGroup = {}) {
  return getAssessmentRootSlotItems(activityGroup).length;
}

export function getDirectAssessmentActivities(activityGroup) {
  return getAssessmentRootSlotItems(activityGroup).filter((item) => !item?.isGroup);
}

function buildOrderBasedAssessmentMapping(leafCount) {
  if (leafCount === DEFAULT_DIRECT_ACTIVITY_PLAN.length) {
    return DEFAULT_DIRECT_AUTHORED_ACTIVITY_MAPPING;
  }

  if (leafCount === Object.keys(DEFAULT_COLLAPSED_AUTHORED_ACTIVITY_MAPPING).length) {
    return DEFAULT_COLLAPSED_AUTHORED_ACTIVITY_MAPPING;
  }

  return {};
}

export function getAssessmentLeafSlots(solutionMenu = {}, rootGroup = {}) {
  const groups = Array.isArray(solutionMenu?.activityGroups) ? solutionMenu.activityGroups : [];
  const byId = groups.reduce((acc, group) => {
    if (group?._id) acc[String(group._id)] = group;
    return acc;
  }, {});

  const slots = [];

  function walkGroup(group, parentGroupId, path = [], seen = new Set()) {
    if (!group?.elements?.length) return;

    if (group?._id) {
      if (seen.has(String(group._id))) return;
      seen.add(String(group._id));
    }

    group.elements.forEach((item, index) => {
      if (!item?._id) return;
      const nextPath = [...path, index + 1];

      if (item.isGroup) {
        const childGroup = byId[String(item._id)];
        walkGroup(childGroup, item._id, nextPath, new Set(seen));
        return;
      }

      slots.push({
        index: slots.length + 1,
        activityId: item._id,
        parentGroupId: parentGroupId || group?._id || null,
        path: nextPath,
        item,
      });
    });
  }

  walkGroup(rootGroup, rootGroup?._id || null);
  return slots;
}

export function getAssessmentMappingForGroup(activityGroup, solutionMenu = {}) {
  const leafSlots = getAssessmentLeafSlots(solutionMenu, activityGroup);
  const nameBasedMapping = buildNameBasedAssessmentMapping(leafSlots, solutionMenu);

  if (nameBasedMapping) {
    return nameBasedMapping;
  }

  // Fallback for legacy/unnamed diagnostic content: the current authored order
  // defines the assessment slots. This fallback only runs when the activity names
  // cannot provide a full 14-key Diagnostic Assessment v1 mapping.
  return buildOrderBasedAssessmentMapping(leafSlots.length || getAssessmentRootSlotCount(activityGroup));
}

export function getAssessmentGroupValidation(solutionMenu, activityGroup) {
  if (!activityGroup?._id) {
    return {
      isValid: false,
      isLevel0: false,
      hasHelp: false,
      message: 'Assessment Level 0 menu row was not found or is ambiguous in this solution menu. Configure exactly one existing menuGroups row with menuLevel 0 for this solution.',
    };
  }

  const row = getAssessmentMenuRow(solutionMenu, activityGroup);
  const isLevel0 = row ? String(row.menuLevel) === '0' : /level\s*0/i.test(String(activityGroup?.name || ''));
  const helpValues = Object.values(activityGroup?.multilangWordsHelp || {}).filter(Boolean);
  const hasHelp = helpValues.some((item) => item?.helpString || item?.helpAudio);
  const hasRootSlots = getAssessmentRootSlotItems(activityGroup).length > 0;
  const leafSlots = getAssessmentLeafSlots(solutionMenu, activityGroup);
  const supportsKnownOrder = leafSlots.length === DEFAULT_DIRECT_ACTIVITY_PLAN.length || leafSlots.length === Object.keys(DEFAULT_COLLAPSED_AUTHORED_ACTIVITY_MAPPING).length;

  if (!isLevel0) {
    return {
      isValid: false,
      isLevel0,
      hasHelp,
      message: 'Assessment activity group must be attached to the selected solution menu as Level 0 before it can be used for online assessment.',
    };
  }

  if (!hasRootSlots || !leafSlots.length) {
    return {
      isValid: false,
      isLevel0,
      hasHelp,
      message: 'Assessment activity group is empty. Add the authored assessment activities/groups before testing.',
    };
  }

  if (!supportsKnownOrder) {
    return {
      isValid: false,
      isLevel0,
      hasHelp,
      message: `Assessment activity mapping is ambiguous. Found ${leafSlots.length} leaf activities. Configure the existing Level 0 group as 14 direct activities or 11 collapsed slots.`,
    };
  }

  return {
    isValid: true,
    isLevel0,
    hasHelp,
    message: hasHelp ? null : 'Assessment activity group has no dedicated help string/audio configured yet.',
  };
}

export function getAssessmentNestedGroupIds(solutionMenu, rootGroup) {
  const ids = new Set();
  const groups = Array.isArray(solutionMenu?.activityGroups) ? solutionMenu.activityGroups : [];
  const byId = groups.reduce((acc, group) => {
    acc[String(group?._id)] = group;
    return acc;
  }, {});

  function visit(groupId) {
    if (!groupId || ids.has(String(groupId))) return;
    ids.add(String(groupId));

    const group = byId[String(groupId)];
    (group?.elements || []).forEach((item) => {
      if (item?.isGroup) visit(item._id);
    });
  }

  visit(rootGroup?._id);
  return Array.from(ids);
}

export function getAssessmentSlotIndexForActivity({ solutionMenu, assessmentRootGroupId, currentActivityGroup, activityId }) {
  const rootGroup = getGroupById(solutionMenu, assessmentRootGroupId) || findFreeTrialActivityGroup(solutionMenu);
  const leafSlots = getAssessmentLeafSlots(solutionMenu, rootGroup);

  if (!leafSlots.length) return null;

  const directIndex = leafSlots.findIndex((slot) =>
    String(slot.activityId) === String(activityId),
  );

  if (directIndex !== -1) return directIndex + 1;

  return null;
}

export function getSuppressedAssessmentActivityIds(attempt, reportSnapshot) {
  const fromSummary = attempt?.summary?.assessmentActivitySourceIds || [];
  const fromReport = reportSnapshot?.report?.assessmentActivitySourceIds || [];
  const fromResults = (attempt?.activityResults || [])
    .map((item) => item?.sourceActivityId || item?.meta?.sourceActivityId)
    .filter(Boolean);

  return Array.from(new Set([...fromSummary, ...fromReport, ...fromResults].map(String).filter(Boolean)));
}

export function getFlattenedExpectedActivityPlan(mapping = DEFAULT_AUTHORED_ACTIVITY_MAPPING) {
  return Object.keys(mapping)
    .map((key) => Number(key))
    .sort((left, right) => left - right)
    .flatMap((key) => mapping[key] || []);
}

export function parseAssessmentOrderFromActivity(activity = {}) {
  const rawLabel = [
    activity?.activityName,
    activity?.name,
    activity?.formattedName,
    activity?.title,
  ]
    .filter(Boolean)
    .join(' ');

  const match = rawLabel.match(/(?:#|number\s*)(\d{1,2})/i) || rawLabel.match(/(^|\s)(\d{1,2})(?=\s|$)/);

  if (!match) return null;
  return Number(match[1] || match[2]);
}

export function getAuthoredAssessmentIndex({
  solutionMenu,
  assessmentRootGroupId,
  currentActivityGroup,
  activityId,
  currentActivity,
}) {
  const slotIndex = getAssessmentSlotIndexForActivity({
    solutionMenu,
    assessmentRootGroupId,
    currentActivityGroup,
    activityId,
  });

  return slotIndex || parseAssessmentOrderFromActivity(currentActivity?.activity || currentActivity || {}) || null;
}

export function buildSyntheticAnswersByQuestion(correctAnswers, totalQuestions = 12) {
  const safeTotal = Math.max(0, Number(totalQuestions) || 0);
  const safeCorrect = Math.max(0, Math.min(safeTotal, Number(correctAnswers) || 0));
  const answers = [];

  for (let index = 0; index < safeTotal; index += 1) {
    answers.push(index < safeCorrect ? 1 : 0);
  }

  return answers;
}

function normalizeBinaryAnswers(answers = []) {
  return answers.map((answer) => (Number(answer || 0) > 0 ? 1 : 0));
}

function collapseAttemptAnswersByQuestion(answers = [], totalQuestions) {
  const targetTotal = Number(totalQuestions);

  if (!Array.isArray(answers) || !Number.isFinite(targetTotal) || targetTotal <= 0) {
    return null;
  }

  const binaryAnswers = normalizeBinaryAnswers(answers);

  if (binaryAnswers.length === targetTotal) {
    return binaryAnswers;
  }

  if (binaryAnswers.length < targetTotal) {
    return null;
  }

  const collapsed = [];
  let hadIncorrectAttempt = false;

  for (let index = 0; index < binaryAnswers.length; index += 1) {
    const isCorrectAttempt = binaryAnswers[index] === 1;

    if (isCorrectAttempt) {
      collapsed.push(hadIncorrectAttempt ? 0 : 1);
      hadIncorrectAttempt = false;

      if (collapsed.length === targetTotal) {
        break;
      }
    } else {
      hadIncorrectAttempt = true;
    }
  }

  return collapsed.length === targetTotal ? collapsed : null;
}

function resolveOrderedAnswersByQuestion(answersByQuestion, totalQuestions) {
  const collapsedAnswers = collapseAttemptAnswersByQuestion(answersByQuestion, totalQuestions);

  return Array.isArray(collapsedAnswers) && collapsedAnswers.length === Number(totalQuestions)
    ? collapsedAnswers
    : null;
}

function buildPhonics4ConservativeAnswers(correctAnswers, totalQuestions) {
  const safeTotal = Math.max(0, Number(totalQuestions) || 0);
  const safeCorrect = Math.max(0, Math.min(safeTotal, Number(correctAnswers) || 0));
  const wrongCount = Math.max(0, safeTotal - safeCorrect);
  const answers = [];

  for (let index = 0; index < safeTotal; index += 1) {
    answers.push(index < wrongCount ? 0 : 1);
  }

  return answers;
}

function pickRawAssessment(stats = {}, currentActivity = {}, activityProgress = {}) {
  const assessmentStats = activityProgress?.assessmentStats || {};
  const rawAssessment = (
    assessmentStats?.rawAssessment ||
    stats?.data?.rawAssessment ||
    activityProgress?.rawAssessment ||
    currentActivity?.data?.rawAssessment ||
    currentActivity?.activity?.rawAssessment ||
    {}
  );

  const answersByQuestion = Array.isArray(rawAssessment.answersByQuestion)
    ? rawAssessment.answersByQuestion
    : Array.isArray(assessmentStats.answersByQuestion)
      ? assessmentStats.answersByQuestion
      : Array.isArray(stats?.data?.answersByQuestion)
        ? stats.data.answersByQuestion
        : null;

  return answersByQuestion ? { ...rawAssessment, answersByQuestion } : rawAssessment;
}

function pickBestFiniteNumber(...values) {
  const numbers = values
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (!numbers.length) return 0;

  const positive = numbers.filter((value) => value > 0);
  return positive.length ? Math.max(...positive) : numbers[0];
}

const DEFAULT_ASSESSMENT_TOTAL_QUESTIONS_BY_KEY = {
  phonics_1_segmenting: 10,
  phonics_2_letter_naming: 10,
  phonics_3_blending: 10,
  phonics_4_word_reading: 12,
};

function resolveAssessmentTotalQuestions(activityKey, measuredTotalQuestions) {
  const measuredTotal = Number(measuredTotalQuestions);
  const hasMeasuredTotal = Number.isFinite(measuredTotal) && measuredTotal > 0;

  if (activityKey === 'phonics_4_word_reading' && hasMeasuredTotal) {
    return measuredTotal;
  }

  const expectedTotal = DEFAULT_ASSESSMENT_TOTAL_QUESTIONS_BY_KEY[activityKey];

  if (Number.isFinite(expectedTotal) && expectedTotal > 0) {
    return expectedTotal;
  }

  return hasMeasuredTotal ? measuredTotal : null;
}

export function buildAssessmentSubmissions({ logicalKeys, stats, activityProgress = {}, currentActivity, isMastered }) {
  const rawAssessment = pickRawAssessment(stats, currentActivity, activityProgress);
  const assessmentStats = activityProgress?.assessmentStats || {};
  const problemsAnsweredCorrectly = pickBestFiniteNumber(
    assessmentStats?.problemsAnsweredCorrectly,
    assessmentStats?.correctAnswers,
    activityProgress?.problemsAnsweredCorrectly,
    activityProgress?.correctAnswers,
    rawAssessment.correctAnswers,
    stats?.data?.problemsAnsweredCorrectly,
    stats?.problemsAnsweredCorrectly,
    stats?.data?.correctAnswers,
    stats?.correctAnswers,
  );
  const allProblemsCount = pickBestFiniteNumber(
    assessmentStats?.allProblemsCount,
    assessmentStats?.totalQuestions,
    activityProgress?.allProblemsCount,
    activityProgress?.totalQuestions,
    rawAssessment.totalQuestions,
    stats?.data?.allProblemsCount,
    stats?.allProblemsCount,
    stats?.data?.totalQuestions,
    stats?.totalQuestions,
  );
  const computedMastered = Boolean(
    isMastered ||
    activityProgress?.mastered ||
    assessmentStats?.isMastered ||
    rawAssessment?.isMastered ||
    (allProblemsCount > 0 && problemsAnsweredCorrectly >= allProblemsCount)
  );
  const star = computedMastered ? 'green' : 'yellow';

  return (logicalKeys || []).map((activityKey) => {
    if (activityKey === 'phonics_4_word_reading') {
      const totalQuestions = resolveAssessmentTotalQuestions(activityKey, allProblemsCount);
      const orderedAnswersByQuestion = resolveOrderedAnswersByQuestion(rawAssessment.answersByQuestion, totalQuestions);
      const answersByQuestion = orderedAnswersByQuestion ||
        buildPhonics4ConservativeAnswers(problemsAnsweredCorrectly, totalQuestions || 12);

      return {
        activityKey,
        payload: {
          answersByQuestion,
          correctAnswers: problemsAnsweredCorrectly,
          totalQuestions,
          rawMetrics: {
            source: 'desktop-assessment-bridge',
            orderedAnswersCaptured: Boolean(orderedAnswersByQuestion),
          },
        },
      };
    }

    if (activityKey.startsWith('reading_')) {
      const readingSpeed = Number(rawAssessment.readingSpeed || stats?.ebookBenchmark?.speed || 0) || null;
      const benchmark = Number(rawAssessment.readingBenchmark || stats?.ebookBenchmark?.benchmark || 0) || null;
      const sharedReadingPayload = {
        star,
        isGreen: computedMastered,
        correctAnswers: problemsAnsweredCorrectly,
        totalQuestions: allProblemsCount || null,
        rawMetrics: {
          source: 'desktop-assessment-bridge',
          benchmark,
          totalWords: stats?.ebookBenchmark?.totalWords || null,
          spentTime: stats?.ebookBenchmark?.spentTime || null,
        },
      };

      if (activityKey.endsWith('_fluency')) {
        return {
          activityKey,
          payload: {
            ...sharedReadingPayload,
            readingSpeed,
            readingSpeedUnit: rawAssessment.readingSpeedUnit || 'wpm',
          },
        };
      }

      return {
        activityKey,
        payload: {
          ...sharedReadingPayload,
          comprehensionCorrectAnswers: problemsAnsweredCorrectly,
          comprehensionTotalQuestions: allProblemsCount || null,
        },
      };
    }

    if (activityKey === 'writing_1') {
      const submittedText = rawAssessment.submittedText || currentActivity?.data?.submittedText || '';

      return {
        activityKey,
        payload: {
          star,
          textLength: submittedText.length || Number(rawAssessment.textLength || 0) || null,
          submittedText: submittedText || null,
          rawMetrics: {
            source: 'desktop-assessment-bridge',
          },
        },
      };
    }

    const totalQuestions = resolveAssessmentTotalQuestions(activityKey, allProblemsCount);

    return {
      activityKey,
      payload: {
        star,
        isGreen: computedMastered,
        correctAnswers: problemsAnsweredCorrectly,
        totalQuestions,
        rawMetrics: {
          source: 'desktop-assessment-bridge',
        },
      },
    };
  });
}

export function collectAssessmentRecommendations(attempt) {
  const summaryRecommendations = Array.isArray(attempt?.summary?.recommendations)
    ? attempt.summary.recommendations
    : [];

  const subSkillFindings = Array.isArray(attempt?.summary?.subSkillFindings)
    ? attempt.summary.subSkillFindings
    : [];

  if (summaryRecommendations.length || subSkillFindings.length) {
    return [...summaryRecommendations, ...subSkillFindings].map((item) => ({
      code: item?.code || '',
      text: item?.text || item?.code || '',
    }));
  }

  const results = Array.isArray(attempt?.activityResults) ? attempt.activityResults : [];

  return results
    .map((item) => item?.recommendation)
    .filter(Boolean)
    .map((item) => ({
      code: item?.code || '',
      text: item?.text || '',
    }));
}

export function getDesktopAssessmentLabels(mode = DESKTOP_ASSESSMENT_MODES.MOBILE) {
  if (normalizeMode(mode) === DESKTOP_ASSESSMENT_MODES.ONLINE) {
    return {
      title: 'Diagnostic Assessment',
      start: 'Start Assessment',
      resume: 'Resume Assessment',
      restart: 'Restart Assessment',
      ready: 'Assessment report ready',
      progress: 'Assessment in progress',
      idle: 'Assessment not started',
      output: 'Assessment report',
    };
  }

  return {
    title: 'Free Trial Assessment',
    start: 'Start Free Trial',
    resume: 'Resume Free Trial',
    restart: 'Restart Assessment',
    ready: 'Custom level ready',
    progress: 'Assessment in progress',
    idle: 'Assessment not started',
    output: 'Custom Level 1',
  };
}
